import { ChangeDetectionStrategy, Component, computed, inject, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { LucideAngularModule } from 'lucide-angular';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { BrandingService } from '../../_core/branding.service';
import { Settings } from '../../_core/settings';

const SOCIAL_ICON_CLASS: Record<string, string> = {
  facebook: 'pi pi-facebook',
  linkedin: 'pi pi-linkedin',
  youtube: 'pi pi-youtube',
  blog: 'pi pi-globe',
};

interface SocialLink {
  name: string;
  link: string;
  iconClass: string;
}

@Component({
  selector: 'app-contact',
  imports: [CardModule, ButtonModule, LucideAngularModule],
  templateUrl: './contact.html',
  styleUrl: './contact.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Contact implements OnInit {
  private readonly $settings = inject(Settings);
  private readonly $branding = inject(BrandingService);
  private readonly router = inject(Router);

  readonly contact = this.$settings.contact;
  readonly isGGM = this.$branding.isGGM;
  readonly sendRequestsEnabled = this.$branding.sendRequestsEnabled;

  readonly displayPhone = computed(() => {
    const phone = this.contact()?.phone;
    return phone ? this.formatPhone(phone) : null;
  });

  readonly socialLinks = computed<SocialLink[]>(() => {
    if (!this.isGGM()) return [];
    const items = this.contact()?.socialMedia ?? [];
    return items.map((item) => ({
      name: item.name,
      link: item.link,
      iconClass: this.resolveSocialIconClass(item.name),
    }));
  });

  async ngOnInit(): Promise<void> {
    await this.$settings.fetchContact();
  }

  goToSendConsultation(): void {
    void this.router.navigate(['/enviar-consulta']);
  }

  resolveSocialIconClass(name: string): string {
    const key = name.trim().toLowerCase();
    return SOCIAL_ICON_CLASS[key] ?? 'pi pi-share-alt';
  }

  formatPhone(phone: string): string {
    const digits = phone.replace(/\D/g, '');
    if (digits.length === 9) {
      return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
    }
    if (digits.length === 11 && digits.startsWith('34')) {
      return `+34 ${digits.slice(2, 5)} ${digits.slice(5, 8)} ${digits.slice(8)}`;
    }
    return phone;
  }

  telHref(phone: string): string {
    return `tel:${phone.replace(/[^\d+]/g, '')}`;
  }
}
